import type { ProjectMembership } from '@deque/jazzband-client';
import {
  MOBILE_PROJECT_TYPES,
  ProjectType,
  type UrlMode
} from '@deque/jazzband-utils';
import {
  AggregateProperties,
  Properties,
  SortDirections,
  type IssuesFilterChangeSource,
  type PropertyConfiguration,
  type SortDirection
} from '@deque/ui-issues';
import type { ImpactValue } from 'axe-core';
import CrossProductAnalytics from '~/common/CrossProductAnalytics';
import type { AxeWatcherProject } from '~/common/api-client';
import type { Automation } from './constants';
// Value import (not type-only): the enum members are read at runtime to
// validate the `language` event property against its closed set — see
// `projectPlatformSelected`.
import type { ExportFormat } from './constants';
import { AutomationSupportedLanguages } from './constants';
import type { ViewByTab } from './hooks/useViewByTab';

// The role values DevHub analytics emits on `member:*` events. Mirrors the
// `ProjectMembership['role']` shape today, but is defined here so the
// analytics wire format is owned by the analytics module — a future rename
// in the membership/API layer can't silently change what reaches Amplitude.
export type MemberRole = 'admin' | 'contributor';

// Compile-time invariant tying the locally-owned `MemberRole` to the API
// source. If the API adds a role (e.g. 'viewer'), the first leg below
// resolves to `boolean` (extends distributes over unions) and the
// assignment fails. If the API drops a role, the second leg flips to
// `false`. In either case the developer must consciously extend or
// remap `MemberRole` — Amplitude is append-only, so silent drift here
// would be permanent.
//
// Use a function parameter to anchor the reference rather than a
// top-level `const` + `void`: the latter trips both `no-void` and
// `@typescript-eslint/no-unused-expressions` lints, and dead-code
// elimination by the bundler would silently drop it. A parameter is a
// real use-site that survives any lint config.
type MemberRoleApiSync = ProjectMembership['role'] extends MemberRole
  ? MemberRole extends ProjectMembership['role']
    ? true
    : false
  : false;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _assertMemberRoleApiSync(_check: MemberRoleApiSync): void {
  // Body intentionally empty — the parameter type is the compile-time
  // assertion. Lint disable is correct here: the parameter MUST be
  // present to anchor the type check, but it intentionally has no
  // runtime use.
}
_assertMemberRoleApiSync(true);

// The shape `setProject` accepts for computing the project-scoped auto-merge
// bundle. `project_id` and `selected_project_type` are required (they drive
// `projectId`/`projectType`/`isMobileProject` on every event); everything else
// is optional so the same call can accept either an `AxeWatcherProject` (from
// `useProject`, which carries `is_owner`/`has_git_information`/
// `is_enterprise_project` but no membership) or an `AxeWatcherProjectDetails`
// (from `useProjectDetails` / `useRequireProjectMembership`, which carries
// `membership_role`). Optional fields that aren't supplied are omitted from
// the merged payload rather than sent as `undefined`.
export type AnalyticsProjectContext = {
  project_id: AxeWatcherProject['project_id'];
  selected_project_type: AxeWatcherProject['selected_project_type'];
  has_git_information?: AxeWatcherProject['has_git_information'];
  is_enterprise_project?: AxeWatcherProject['is_enterprise_project'];
  is_owner?: AxeWatcherProject['is_owner'];
  membership_role?: ProjectMembership['role'] | null;
};

// Maps the optional `AnalyticsProjectContext` source keys (which mirror the
// snake_case shapes returned by `useProject` / `useProjectDetails`) to the
// camelCase Amplitude property names we ship on event payloads. Iterated
// inside `defaultData` to merge each optional field only when supplied.
// `membership_role` is intentionally not in this list — its skip semantic
// is "falsy" rather than "undefined" because the type is `role | null`.
//
// `is_owner` → `isApiKeyOwner`: true when the current user created the
// project's API key. This is NOT a project-membership role (DevHub has no
// "project owner" concept — use `userProjectRole` for admin/contributor and
// `isOrgAdmin` for enterprise admins). It distinguishes the user whose
// sessions/scans push into the project from other interactors.
const OPTIONAL_PROJECT_FIELD_MAP = [
  ['has_git_information', 'hasGitIntegration'],
  ['is_enterprise_project', 'isEnterpriseProject'],
  ['is_owner', 'isApiKeyOwner']
] as const satisfies ReadonlyArray<
  readonly [keyof AnalyticsProjectContext, string]
>;

export enum AmplitudeEvents {
  VIEWED_CREATE_PROJECT_PAGE = 'page:create',
  CREATED_PROJECT = 'project:create',
  VIEWED_CHOOSE_PROJECT_DATA_SOURCE_PAGE = 'page:choosedatasource',
  VIEWED_CHOOSE_PROJECT_NAME_PAGE = 'page:chooseProjectName',
  VIEWED_CHOOSE_PROJECT_LANGUAGE_PAGE = 'page:chooseLanguage',
  VIEWED_CHOOSE_PROJECT_TESTING_PLATFORM_PAGE = 'page:chooseTestingPlatform',
  PROJECT_CREATION_CANCEL = 'project:cancel',
  // `page:projectDetails` is fired by the single InstallationInstruction
  // component, which serves both the post-create step and the settings/details
  // page. Its `context: 'create' | 'settings'` property disambiguates the two
  // (issue #15162) — `context: 'create'` is the post-create "completed setup"
  // signal. The old `page:completedInstructions` event was never wired up
  // (0 events in dev) and has been retired in favour of this; do not re-add it.
  VIEWED_PROJECT_DETAILS_PAGE = 'page:projectDetails',
  VIEWED_PROJECTS_PAGE = 'page:projects',
  VIEWED_BRANCHES_PAGE = 'page:branches',
  SHARED_PAGE = 'share',
  VIEWED_COMMITS_PAGE = 'page:commits',
  VIEWED_GITLESS_PAGE = 'page:gitless',
  VIEWED_MOBILE_ANALYZER_PROJECT_PAGE = 'page:mobileAnalyzerProject',
  VIEWED_ISSUES_PAGE = 'page:issues',
  VIEWED_ISSUE_PAGE = 'page:issue',
  EXPORT_ISSUES = 'issues:export',
  VIEWED_MEMBERS_PAGE = 'page:members',
  MEMBER_ADD = 'member:add',
  MEMBER_ROLE_CHANGE = 'member:roleChange',
  MEMBER_REMOVE = 'member:remove',
  // Event-name strings below come straight from the DevHub Amplitude
  // analytics plan (epic dequelabs/results-team#340). The shapes mix
  // triple-segment (`project:config:edit`) and double-segment
  // (`project:instructionsViewed`) on purpose —
  // the plan is the source of truth, not an internal convention.
  // Renaming a shipped event breaks Amplitude dashboards, so leave
  // these as-is unless the analytics owner approves a rename.
  PROJECT_CONFIG_EDIT = 'project:config:edit',
  PROJECT_INSTRUCTIONS_VIEWED = 'project:instructionsViewed',
  PROJECT_ID_COPY = 'project:idCopy',
  PROJECT_SERVER_URL_COPY = 'project:serverUrlCopy',
  BRANCHES_FILTER_BY_MEMBER = 'branches:filterByMember',
  BRANCH_SELECT = 'branch:select',
  COMMIT_SELECT = 'commit:select',
  GITLESS_FILTER_BY_MEMBER = 'gitless:filterByMember',
  SESSION_SELECT = 'session:select',
  MOBILE_EXPORT_MODAL_OPENED = 'mobile:export:modalOpen',
  MOBILE_EXPORT_FORMAT_SELECTED = 'mobile:export:formatSelect',
  MOBILE_EXPORT_COMPLETED = 'mobile:export:complete',
  MOBILE_EXPORT_FAILED = 'mobile:export:fail',
  MOBILE_CONFIG_MISMATCH_BANNER_SHOWN = 'mobileIssuesListPage:configMismatchBannerShown',
  PROJECT_OPEN_FROM_LIST = 'project:openFromList',
  PROJECT_CONFIG_FROM_LIST = 'project:configFromList',
  PROJECT_MEMBERS_FROM_LIST = 'project:membersFromList',
  MOBILE_ANALYZER_VALID_SESSION = 'session:mobileAnalyzerValid',
  ISSUES_FILTER_APPLIED = 'issues:filterApplied',
  PROJECT_ADD_NEW_FROM_LIST = 'project:addNewFromList',
  PROJECTS_SORT_CHANGED = 'projects:sortChanged',
  PROJECTS_SEARCH_APPLIED = 'projects:searchApplied',
  PROJECTS_PAGINATE = 'projects:paginate',
  // Create Project wizard funnel (issue #14312). `wizard:integrationSelected`
  // fires when the user picks a card on the "Choose your integration method"
  // step; the chosen integration source is the `ProjectType` slug that lands in
  // the wizard's `source=` URL param (e.g. `axe-devtools-watcher`). The ticket's
  // separate `wizard:sourceAppSelected` event is intentionally NOT implemented
  // — that step does not exist as a distinct UI interaction (the integration
  // cards are the only first-step selection). See the ticket comment for the
  // re-spec rationale.
  //
  // The integration-source value is emitted under the single property
  // `projectType` across the whole wizard funnel (issue #15162) —
  // `page:chooseProjectName`, `page:chooseLanguage`, `page:chooseTestingPlatform`,
  // `project:languageSelected`, `project:platformSelected`,
  // `wizard:integrationSelected`, `project:create`, `project:cancel`, and
  // `page:projectDetails`. Previously it was split across `datasource` /
  // `sourceApp` / `integrationType` / `projectType`; converging on `projectType`
  // lets cross-event funnels group/slice by integration source without a join.
  PROJECT_CREATE_START = 'project:createStart',
  WIZARD_INTEGRATION_SELECTED = 'wizard:integrationSelected',
  // `project:languageSelected` fires on the wizard's "Language" step (Watcher
  // only); `project:platformSelected` fires on the "Platform" (Watcher) /
  // "Framework" (Mobile) step. Both re-fire on every pick so funnel analysis
  // can see selection changes.
  PROJECT_LANGUAGE_SELECTED = 'project:languageSelected',
  PROJECT_PLATFORM_SELECTED = 'project:platformSelected',
  // Fires only once the option saves: a rejected save is not adoption.
  PROJECT_UNIQUE_URLS_SELECT = 'project:uniqueUrls:select',
  ISSUE_NAVIGATE = 'issue:navigate',
  ISSUE_COPY_LINK = 'issue:copyLink',
  ISSUE_JIRA_OPEN = 'issue:jiraOpen',
  ISSUE_JIRA_TICKET_CREATE = 'issue:jiraTicketCreate',
  // V3 issues list (results-team#431).
  ISSUES_VIEW_BY_TAB_CHANGE = 'issues:viewByTabChange',
  ISSUES_ALL_FILTERS_OPEN = 'issues:allFiltersOpen',
  ISSUES_SORT_CHANGED = 'issues:sortChanged',
  RULE_VIEW_ISSUES = 'rule:viewIssues',
  ISSUE_OPEN = 'issue:open',
  ISSUE_FLYOUT_CLOSE = 'issue:flyoutClose',
  ISSUE_OPEN_IN_NEW_TAB = 'issue:openInNewTab',
  ISSUES_COPY_LINK = 'issues:copyLink',
  ISSUES_EXPORT_MODAL_OPEN = 'issues:exportModalOpen',
  ISSUES_EXPORT_FAIL = 'issues:exportFail',
  // Switching View-by tabs is otherwise untracked, so these are new events
  // rather than an extension.
  ISSUE_GROUPS_VIEW = 'issueGroups:view',
  ISSUE_GROUPS_SUB_VIEW_SELECT = 'issueGroups:subViewSelect',
  ISSUE_GROUPS_OPEN = 'issueGroups:open',
  // Cross-cutting friction signal. Fires when an empty / zero-row state is
  // shown on a DevHub page; the `context` property says
  // which page. High-value for Customer Success — surfaces stuck or
  // not-yet-activated customers that are otherwise invisible. NOTE the trigger
  // is page-specific, so dashboards must not read every context as a project
  // "no data at all" signal:
  //   - `projects` / `branches` / `gitless` — no scan data for the current
  //     project (or the selected member): an activation-failure signal.
  //   - `commits` — no commits in the current branch view (list-level; the
  //     project may have data on other branches).
  //   - `issues` — no unique issues in the current page-state/filter view: can
  //     be filter-induced, not necessarily zero data.
  EMPTY_STATE_SHOWN = 'emptyState:shown'
}

export const ExportArtifactFormat = {
  MobileJson: 'mobile_json',
  AxeUniversalJson: 'axe_universal_json',
  WebJson: 'web_json',
  Csv: 'csv'
} as const;

export type ExportArtifactFormat =
  (typeof ExportArtifactFormat)[keyof typeof ExportArtifactFormat];

export type ExportFailureReason = 'timeout' | 'backend_error' | 'network_error';

export enum IntegrationMethod {
  MobileAnalyzer = 'mobile_analyzer',
  MobileSdks = 'mobile_sdks',
  Watcher = 'watcher',
  WebApis = 'web_apis'
}

export type IssuesFilterType =
  | 'rule'
  | 'impact'
  | 'scan'
  | 'pagestate'
  | 'test';

/**
 * Which V3 issues-list surface an event came from. `group` is the Issue groups
 * tab; `groupDetail` is one group's own page.
 */
export type IssuesAnalyticsView = ViewByTab | 'ruleDetail' | 'groupDetail';

export type IssuesFilterAction = 'applied' | 'removed';

export type IssuesSortColumn =
  | 'rule'
  | 'impact'
  | 'issueCount'
  | 'location'
  | 'scanName';

// Columns without an entry (row actions, custom columns) are never reported.
const SORT_COLUMNS = new Map<PropertyConfiguration['type'], IssuesSortColumn>([
  [Properties.Rule.type, 'rule'],
  [Properties.Impact.type, 'impact'],
  [AggregateProperties.IssueCount.type, 'issueCount'],
  [Properties.Location.type, 'location'],
  [Properties.ScanName.type, 'scanName']
]);

export type IssueOpenTrigger = 'viewIssue' | 'location';

export type IssueFlyoutCloseMethod = 'closeButton' | 'dismiss';

export type IssuesExportViewLevel = 'topLevel' | 'drillDown';

/** `'json'` is Web JSON — kept from before the V3 rename so charts continue. */
export type IssuesExportFormat =
  | ExportFormat
  | typeof ExportArtifactFormat.AxeUniversalJson
  | typeof ExportArtifactFormat.MobileJson;

export type IssuesExportFailureReason = ExportFailureReason | 'client_error';

export type PipelineState = 'pipeline' | 'non_pipeline' | 'unknown';

export const pipelineState = (
  isCanonicalSource: boolean | null
): PipelineState => {
  if (isCanonicalSource === null) {
    return 'unknown';
  }
  return isCanonicalSource ? 'pipeline' : 'non_pipeline';
};

/**
 * Create-Project wizard steps, used as the `stepAbandoned` value on
 * `project:cancel`. Only the `StartupSteps` form steps — `projectName`,
 * `language`, `testingPlatform` — are emitted today: cancelling on one fires
 * `projectCreationCancel` with the matching `stepId`. `dataSource` (the
 * source-app selection screen in `StartupFlowWizard`) is reserved for when that
 * screen tracks abandonment; it has no `project:cancel` emission yet, so the
 * value is not currently sent and first-step abandonment is untracked. See
 * dequelabs/walnut#14310.
 */
export type WizardStepName =
  | 'dataSource'
  | 'projectName'
  | 'language'
  | 'testingPlatform'
  | 'uniqueUrls';

// The DevHub pages that can show a zero-data empty state. Shipped as the
// `context` property on `emptyState:shown`. A closed union so
// call sites can't drift the vocabulary — a new empty-state surface must
// consciously extend this set.
export type EmptyStateContext =
  | 'projects'
  | 'branches'
  | 'commits'
  | 'issues'
  // The Issue groups tab rendered with no groups. Distinct from `issues`:
  // the scan has issues, they just all sit on elements that failed once.
  | 'issueGroups'
  | 'gitless';

// Bucketed accessibility-score ranges shipped on the `page:issues` event for
// mobile sessions (issue #14297). The raw score is intentionally never sent —
// only the bucket — so dashboards can plot distribution without exposing a
// per-session value. Boundaries are lower-inclusive / upper-exclusive, except
// the top bucket which includes 100.
export type A11yScoreBucket = '0-50' | '50-70' | '70-85' | '85-100';

const bucketA11yScore = (score: number): A11yScoreBucket => {
  if (score < 50) {
    return '0-50';
  }
  if (score < 70) {
    return '50-70';
  }
  if (score < 85) {
    return '70-85';
  }
  return '85-100';
};

/**
 * Per-row payload for the Projects-list row events. The plan's
 * `isApiKeyOwner` / `userProjectRole` are omitted because the list endpoint
 * type doesn't carry them — see dequelabs/walnut#14692. (`projectState` was
 * also on that list; it has since been removed outright — walnut#15914.)
 */
export type ProjectListRowAnalytics = {
  projectId: string;
  projectType: string | null;
  hasGitIntegration: boolean;
  isEnterpriseProject: boolean;
};

class AxeDevtoolsWatcherAnalytics extends CrossProductAnalytics<AmplitudeEvents> {
  // `setEnterprise` is inherited from the shared `Analytics` base, which owns
  // the `enterprise` field and the Amplitude user-property push. DevHub-only
  // behavior here is that the `defaultData` getter below auto-merges
  // `enterpriseId` / `rootGroup` onto every event payload.
  private project: AnalyticsProjectContext | null = null;

  /**
   * Registers the project the user is currently scoped to, so subsequent
   * events auto-merge its identity and metadata via `defaultData`. Pass `null`
   * when leaving a project-scoped route so later events (e.g. on the projects
   * list) don't get a stale project context.
   */
  public setProject(project: AnalyticsProjectContext | null) {
    this.project = project;
  }

  public viewedCreateProjectPage(referrer: string) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_CREATE_PROJECT_PAGE,
      data: { ...this.defaultData, referrer }
    });
  }

  /**
   * @param projectType Which project type was created.
   * @param platform Which platform was the project created for. Omitted for
   *   project types without a platform (e.g. mobile analyzer).
   * @param language Which language was selected. Omitted for project types
   *   without a language step (e.g. mobile analyzer, API/CLI).
   * @param timeToCompleteSec Whole, non-negative seconds from the wizard's
   *   first step mount to a successful create. Omitted when not measured or not
   *   a finite, non-negative number.
   *
   * `integrationType` and `hasGitIntegration` are intentionally NOT sent here:
   * the create wizard has no git-provider step and git is connected later on
   * the project config page, so neither is knowable at creation. They are
   * captured by the project-scoped auto-merge on later events instead. See
   * dequelabs/walnut#14310.
   */

  public createdProject(
    projectType: ProjectType,
    platform?: Automation,
    language?: AutomationSupportedLanguages,
    timeToCompleteSec?: number
  ) {
    const data = structuredClone(this.defaultData);
    data.projectType = projectType;

    if (platform) {
      data.platform = platform;
    }

    if (language) {
      data.language = language;
    }

    if (
      typeof timeToCompleteSec === 'number' &&
      Number.isFinite(timeToCompleteSec) &&
      timeToCompleteSec >= 0
    ) {
      data.timeToCompleteSec = timeToCompleteSec;
    }

    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.CREATED_PROJECT,
      data
    });
  }

  /**
   * @param projectCount Number of projects in the org's list at page-load.
   * @param sortBy The active sort column key (omitted when not sorted).
   * @param hasActiveSearch `true` when a search value is active. Only the
   *   boolean is sent; the raw search string is never forwarded (PII).
   */
  public viewedProjectsPage(
    projectCount?: number,
    sortBy?: string,
    hasActiveSearch?: boolean
  ) {
    const data = structuredClone(this.defaultData);

    if (typeof projectCount === 'number') {
      data.projectCount = projectCount;
    }

    if (sortBy) {
      data.sortBy = sortBy;
    }

    if (typeof hasActiveSearch === 'boolean') {
      data.hasActiveSearch = hasActiveSearch;
    }

    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_PROJECTS_PAGE,
      data
    });
  }

  /**
   * @param owner Is the viewer of the page the owner of the project?
   * @param projectId Project Id
   */

  public viewedBranchesPage(owner: boolean, projectId: string) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_BRANCHES_PAGE,
      data: { ...this.defaultData, owner, projectId }
    });
  }

  /**
   * @param page Which page was shared?
   * @param projectId Project Id
   * @param whole Was the entire record shared?
   */

  public sharedPage(page: string, projectId: string, whole?: boolean) {
    const data: Record<string, unknown> = {
      ...this.defaultData,
      page,
      projectId
    };

    if (whole !== undefined) {
      data.whole = whole;
    }

    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.SHARED_PAGE,
      data
    });
  }

  /**
   * @param owner Is the viewer of the page the owner of the project?
   * @param projectId Project Id
   */

  public viewedCommitsPage(owner: boolean, projectId: string) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_COMMITS_PAGE,
      data: { ...this.defaultData, owner, projectId }
    });
  }

  /**
   * @param owner Is the viewer of the page the owner of the project?
   * @param projectId Project Id
   */

  public viewedGitlessPage(owner: boolean, projectId: string) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_GITLESS_PAGE,
      data: { ...this.defaultData, owner, projectId }
    });
  }

  /**
   * @param owner Is the viewer of the page the owner of the project?
   * @param projectId Project Id
   */

  public viewedMobileAnalyzerProjectPage(owner: boolean, projectId: string) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_MOBILE_ANALYZER_PROJECT_PAGE,
      data: { ...this.defaultData, owner, projectId }
    });
  }

  /**
   * @param owner Is the viewer of the page the owner of the project?
   * @param projectId Project Id
   * @param ruleId The axe rule id of the issue being viewed (e.g.
   *   `'color-contrast'`). Identifies *which* issue the user reached — the
   *   deepest point in the activation funnel.
   * @param impact The rule's impact severity (`'critical'` / `'serious'` /
   *   `'moderate'` / `'minor'`). Omitted when the result carries no impact.
   *   Forwarded because it's free at the call site and lets dashboards slice
   *   issue engagement by severity; it is rule-level, not node-level, so it
   *   carries no element/selector detail.
   */

  public viewedIssuePage(
    owner: boolean,
    projectId: string,
    ruleId: string,
    impact?: ImpactValue
  ) {
    const data: Record<string, unknown> = {
      ...this.defaultData,
      owner,
      projectId,
      ruleId
    };

    if (impact) {
      data.impact = impact;
    }

    if (this.integrationMethod) {
      data.integrationMethod = this.integrationMethod;
    }

    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_ISSUE_PAGE,
      data
    });
  }

  /**
   * User clicked the previous/next issue button in the Single Issue page
   * header (the `onPreviousIssue` / `onNextIssue` controls).
   *
   * @param direction `'prev'` or `'next'` — which control was clicked.
   */
  public issueNavigate(direction: 'prev' | 'next') {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_NAVIGATE,
      data: { ...this.defaultData, direction }
    });
  }

  /**
   * User copied the shareable link to the current issue (the header's
   * "Copy issue link" action).
   *
   * Minimal payload by design: the copied URL embeds the project / rule /
   * session already carried by the auto-merged context, and the raw URL is
   * not a useful, non-redundant breakdown — so this event rides only the
   * auto-merged project/enterprise context.
   */
  public issueCopyLink() {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_COPY_LINK,
      data: { ...this.defaultData }
    });
  }

  /**
   * User clicked a Jira integration button on the Single Issue page — either
   * the "Add to Jira" button that opens the create-ticket modal, or the
   * "Open in Jira" link to an already-linked ticket.
   *
   * @param hasExistingTicket `true` when the issue already has a linked Jira
   *   ticket at click time (the "Open in Jira" link), `false` when opening the
   *   create flow. The Jira ticket id / key / URL is never sent — only this
   *   outcome dimension.
   */
  public issueJiraOpen(hasExistingTicket: boolean) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_JIRA_OPEN,
      data: { ...this.defaultData, hasExistingTicket }
    });
  }

  /**
   * Fires once per completed Jira create attempt — on the API response, not
   * the click that opens the create modal.
   *
   * @param isSuccessful `true` when the ticket was created, `false` on
   *   failure/error. No ticket id, key, URL, or user identity is sent. Emitted
   *   as the `success` event property (the name the analytics plan specifies).
   */
  public issueJiraTicketCreate(isSuccessful: boolean) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_JIRA_TICKET_CREATE,
      data: { ...this.defaultData, success: isSuccessful }
    });
  }

  /**
   * @param owner Is the viewer of the page the owner of the project?
   * @param whole Was the entire record shared?
   * @param projectId Project Id
   * @param a11yScore Accessibility score (0–100) for the session, or `null`
   *   when not yet computed. Only enriches the event on mobile sessions; the
   *   raw value is bucketed and never sent as-is.
   * @param issueCount Total number of unique issues for the session at
   *   page-load, before any filtering. Both issues-list surfaces report this
   *   unfiltered total so the value is comparable across them. Omitted when
   *   unknown.
   * @param filterApplied `true` when the user arrived with an explicit issue
   *   filter active (e.g. a deep-linked `issue_filters`/`page_state` URL
   *   param); auto-applied defaults do not count. Only the boolean is sent —
   *   never the filter values. Omitted when unknown.
   */

  public viewedIssuesPage(
    owner: boolean,
    whole: boolean,
    projectId: string,
    a11yScore?: number | null,
    issueCount?: number,
    filterApplied?: boolean
  ) {
    const data = structuredClone(this.defaultData);
    data.owner = owner;
    data.whole = whole;
    data.projectId = projectId;

    // Accessibility-score enrichment (issue #14297) is mobile-only: the score
    // is displayed exclusively on mobile session reports, so we only analyze
    // it there. Web sessions receive a score from the API but must not carry
    // these properties in this work. `hasA11yScore` is `false` (not omitted)
    // for a mobile session whose score is null/empty-state, so coverage
    // dashboards can distinguish "no score yet" from "not a mobile session".
    if (this.isMobileProject) {
      if (typeof a11yScore === 'number' && Number.isFinite(a11yScore)) {
        data.hasA11yScore = true;
        data.a11yScoreBucket = bucketA11yScore(a11yScore);
      } else {
        data.hasA11yScore = false;
        data.a11yScoreBucket = null;
      }
    }

    if (this.integrationMethod) {
      data.integrationMethod = this.integrationMethod;
    }

    if (typeof issueCount === 'number') {
      data.issueCount = issueCount;
    }

    if (typeof filterApplied === 'boolean') {
      data.filterApplied = filterApplied;
    }

    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_ISSUES_PAGE,
      data
    });
  }

  /**
   * @param numberOfSupportedSources How many source apps the user can choose
   *   between on the data-source step — `1` for a single-product license, `2`
   *   for a multi-license (e.g. DevHub + Mobile). Omitted when unknown or not a
   *   positive count: a `0` (e.g. an org-admin bypassing with no personal
   *   license) is not a meaningful choice count and is dropped rather than sent.
   */
  public viewedChooseProjectDataSourcePage(numberOfSupportedSources?: number) {
    const data = structuredClone(this.defaultData);

    if (
      typeof numberOfSupportedSources === 'number' &&
      Number.isFinite(numberOfSupportedSources) &&
      numberOfSupportedSources > 0
    ) {
      data.numberOfSupportedSources = numberOfSupportedSources;
    }

    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_CHOOSE_PROJECT_DATA_SOURCE_PAGE,
      data
    });
  }

  /**
   * @param projectId Project Id
   * @param projectType The selected integration source (a `ProjectType` slug).
   *   Standardized property name across the wizard funnel (issue #15162).
   */
  public viewedChooseProjectNamePage(
    projectId?: string,
    projectType?: ProjectType
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_CHOOSE_PROJECT_NAME_PAGE,
      data: { ...this.defaultData, projectId, projectType }
    });
  }

  /**
   * @param projectId Project Id
   * @param projectType The selected integration source (a `ProjectType` slug).
   *   Standardized property name across the wizard funnel (issue #15162).
   */
  public viewedChooseProjectLanguagePage(
    projectId?: string,
    projectType?: ProjectType
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_CHOOSE_PROJECT_LANGUAGE_PAGE,
      data: { ...this.defaultData, projectId, projectType }
    });
  }

  /**
   * @param projectId Project Id
   * @param projectType The selected integration source (a `ProjectType` slug).
   *   Standardized property name across the wizard funnel (issue #15162).
   * @param language Which language was chosen?
   */
  public viewedChooseProjectTestingPlatformPage(
    projectId?: string,
    projectType?: ProjectType,
    language?: string
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_CHOOSE_PROJECT_TESTING_PLATFORM_PAGE,
      data: { ...this.defaultData, projectId, projectType, language }
    });
  }

  /**
   * @param projectId Project Id
   * @param language Which language was chosen? (user can cancel at any step)
   * @param platform Which platform was chosen? (user can cancel at any step)
   * @param stepAbandoned Which wizard step the user cancelled on. Omitted when
   *   unknown.
   * @param projectType The selected integration source (a `ProjectType` slug).
   *   Standardized property name across the wizard funnel (issue #15162);
   *   replaces the previous redundant `datasource` property (same value).
   *   Omitted before a source app is chosen.
   */
  public projectCreationCancel(
    projectId?: string | null,
    language?: string,
    platform?: string,
    stepAbandoned?: WizardStepName,
    projectType?: ProjectType
  ) {
    const data = structuredClone(this.defaultData);

    if (projectId) {
      data.projectId = projectId;
    }

    if (language) {
      data.language = language;
    }

    if (platform) {
      data.platform = platform;
    }

    if (stepAbandoned) {
      data.stepAbandoned = stepAbandoned;
    }

    if (projectType) {
      data.projectType = projectType;
    }

    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_CREATION_CANCEL,
      data
    });
  }

  /**
   * Fired by the InstallationInstruction component, which serves both the
   * post-create step and the settings/details page.
   *
   * @param projectId Project Id
   * @param projectType The selected integration source (a `ProjectType` slug).
   *   Standardized property name across the wizard funnel (issue #15162). This
   *   page is project-scoped, so `defaultData` already carries `projectType`;
   *   the explicit value is only spread in when present, so a `null`
   *   `selected_project_type` is not overwritten with `undefined`.
   * @param language Which language was chosen?
   * @param platform Which platform was chosen?
   * @param context How the page was reached: `'create'` (arrived from a
   *   successful project creation — the post-create "completed setup" signal)
   *   vs `'settings'` (opened the existing project's details/settings). See
   *   issue #15162; this replaces the never-wired `page:completedInstructions`.
   */
  public viewedProjectDetailsPage(
    projectId?: string,
    projectType?: ProjectType,
    language?: string,
    platform?: string,
    context?: 'create' | 'settings'
  ) {
    const data = structuredClone(this.defaultData);

    if (projectId) {
      data.projectId = projectId;
    }

    if (projectType) {
      data.projectType = projectType;
    }

    if (language) {
      data.language = language;
    }

    if (platform) {
      data.platform = platform;
    }

    if (context) {
      data.context = context;
    }

    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_PROJECT_DETAILS_PAGE,
      data
    });
  }

  public exportIssues(
    projectId: string,
    format: IssuesExportFormat,
    details?: { viewLevel: IssuesExportViewLevel; durationMs: number }
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.EXPORT_ISSUES,
      data: { ...this.defaultData, projectId, format, ...details }
    });
  }

  private sendMobileExportEvent(
    event: AmplitudeEvents,
    data: Record<string, unknown> = {}
  ) {
    if (!this.isMobileProject) {
      return;
    }
    return this.sendAmplitudeEvent({
      event,
      data: { ...this.defaultData, ...data }
    });
  }

  public mobileExportModalOpened() {
    return this.sendMobileExportEvent(
      AmplitudeEvents.MOBILE_EXPORT_MODAL_OPENED
    );
  }

  /**
   * The user committed to a server-served format — either by confirming it in
   * the export modal, or by clicking the single-format export button on the V3
   * issues list, which bypasses the modal entirely.
   * @param format Which format was chosen.
   */
  public mobileExportFormatSelected(format: ExportArtifactFormat) {
    return this.sendMobileExportEvent(
      AmplitudeEvents.MOBILE_EXPORT_FORMAT_SELECTED,
      { format }
    );
  }

  /**
   * An export download completed successfully. The Universal JSON export also
   * fires this for its page-built fallback, which is logged separately.
   * @param format Which format was exported.
   * @param sessionScanCount Number of scans in the exported session.
   * @param durationMs Wall-clock time from request start to download.
   */
  public mobileExportCompleted(
    format: ExportArtifactFormat,
    sessionScanCount: number,
    durationMs: number
  ) {
    return this.sendMobileExportEvent(AmplitudeEvents.MOBILE_EXPORT_COMPLETED, {
      format,
      sessionScanCount,
      durationMs
    });
  }

  /**
   * An export request failed (backend error, network failure, or timeout).
   * @param format Which format was being exported.
   * @param failureReason Why the export failed.
   * @param durationMs Wall-clock time from request start to failure.
   */
  public mobileExportFailed(
    format: ExportArtifactFormat,
    failureReason: ExportFailureReason,
    durationMs: number
  ) {
    return this.sendMobileExportEvent(AmplitudeEvents.MOBILE_EXPORT_FAILED, {
      format,
      failureReason,
      durationMs
    });
  }

  /**
   * @param memberCount Total number of project members on this project.
   * @param adminCount Number of members with the `admin` role.
   */
  public viewedMembersPage(memberCount: number, adminCount: number) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.VIEWED_MEMBERS_PAGE,
      data: { ...this.defaultData, memberCount, adminCount }
    });
  }

  /**
   * @param assignedRole Role assigned to the added member.
   */
  public memberAdd(assignedRole: MemberRole) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.MEMBER_ADD,
      data: { ...this.defaultData, assignedRole }
    });
  }

  /**
   * @param fromRole Role the member held before the change.
   * @param toRole Role the member was changed to.
   *
   * No-op when `fromRole === toRole`. The guard lives here (at the
   * analytics-class boundary) rather than only at hook/call-site, so the
   * "no event for a no-op role change" invariant holds for every caller,
   * not just callers that route through `useMemberAnalytics`.
   */
  public memberRoleChange(fromRole: MemberRole, toRole: MemberRole) {
    if (fromRole === toRole) {
      return;
    }
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.MEMBER_ROLE_CHANGE,
      data: { ...this.defaultData, fromRole, toRole }
    });
  }

  /**
   * @param removedRole Role the removed member held.
   * @param wasSelfRemoval `true` if the actor's row was the one being
   *   removed (Leave project, or an admin removing their own row),
   *   `false` if an admin removed someone else. Source: the
   *   `isViewingOwnRow` boolean at the call site. Note the edge case
   *   that an admin removing their own row is also `wasSelfRemoval=true`
   *   — both lose access as a result, which is the metric this property
   *   is designed to capture, but the property name makes that
   *   ambiguity explicit (vs. the previous `wasSelf`, which read as
   *   "voluntary departure only").
   */
  public memberRemove(removedRole: MemberRole, wasSelfRemoval: boolean) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.MEMBER_REMOVE,
      data: { ...this.defaultData, removedRole, wasSelfRemoval }
    });
  }

  public projectConfigEdit() {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_CONFIG_EDIT,
      data: { ...this.defaultData }
    });
  }

  /**
   * @param projectType The project's `ProjectType` (raw enum value, not the
   *   display name). Standardized property name across the activation funnel
   *   (issue #15162) — previously emitted as `sourceApp`. The auto-merged
   *   `projectType` already carries the same value; the explicit property is
   *   kept so dashboards can filter on the event-property name without relying
   *   on the auto-merge bundle, matching the wizard-funnel events.
   */
  public projectInstructionsViewed(projectType: ProjectType) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_INSTRUCTIONS_VIEWED,
      data: { ...this.defaultData, projectType }
    });
  }

  /**
   * Fired when the user copies the Project ID from the Project Config page.
   *
   * Minimal payload by design: the event name distinguishes this from the
   * Server URL copy, and the copied value *is* the project id — already
   * carried by the auto-merged `projectId` context. Re-sending it would be
   * redundant, so this event rides only the auto-merged project/enterprise
   * context.
   */
  public projectIdCopy() {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_ID_COPY,
      data: { ...this.defaultData }
    });
  }

  /**
   * Fired when the user copies the Server URL from the Project Config page.
   *
   * Minimal payload by design: the event name distinguishes this from the
   * Project ID copy, and the copied value is just the page origin, which adds
   * no useful, non-redundant breakdown. This event rides only the
   * auto-merged project/enterprise context.
   */
  public projectServerUrlCopy() {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_SERVER_URL_COPY,
      data: { ...this.defaultData }
    });
  }

  /**
   * Fired when the user picks an option in the Branches page's "View runs
   * from" dropdown (internally still called the user dropdown, hence the
   * `branches:filterByMember` event name).
   *
   * `filterType` distinguishes the four bucket categories:
   *   - `'pipeline'` — the canonical-source bucket (CI runs across all
   *     non-default branches, ungrouped)
   *   - `'member'` — a specific contributor's API key (one entry per
   *     API key, so a contributor with N keys has N options)
   *   - `'no-data'` — a contributor who hasn't submitted data yet
   *   - `'unknown'` — the orphaned-API-keys bucket
   *
   * The selected member's identity is intentionally not sent — auto-merged
   * `userProjectRole` describes the current viewer, not the filtered member.
   */
  public branchesFilterByMember(
    filterType: 'pipeline' | 'member' | 'no-data' | 'unknown'
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.BRANCHES_FILTER_BY_MEMBER,
      data: { ...this.defaultData, filterType }
    });
  }

  /**
   * @param isDefaultBranch Whether the selected branch is the project's default branch.
   * @param target Which navigation link the user clicked: `'commits'` for the
   *   branch title or "View commits" button, `'issues'` for the "View issues"
   *   button. Lets us distinguish browse vs. drill-in funnels.
   */
  public branchSelect(isDefaultBranch: boolean, target: 'commits' | 'issues') {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.BRANCH_SELECT,
      data: { ...this.defaultData, isDefaultBranch, target }
    });
  }

  /**
   * Fired when the user clicks a commit row on the Commits page to navigate
   * into the commit's issues view.
   *
   * @param hasPreviousCommit Whether the clicked commit has a previous
   *   commit to compare against. Distinguishes "investigates a
   *   regression" (true) from "reviews a first/orphan run" (false).
   * @param isCanonicalSource Whether the commit was made via the
   *   canonical (pipeline) source. Reflects the inherited URL-param
   *   filter state and lets dashboards slice drill-ins by source even
   *   though the Commits page does not surface its own filter UI today.
   *
   * Both properties are derived at the call site from data already in
   * memory; neither carries member identity or PII.
   */
  public commitSelect(hasPreviousCommit: boolean, isCanonicalSource: boolean) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.COMMIT_SELECT,
      data: {
        ...this.defaultData,
        hasPreviousCommit,
        isCanonicalSource
      }
    });
  }

  /**
   * User picked an option in the ViewByMember dropdown on the Gitless page.
   * `filterType` distinguishes the bucket categories shown on Gitless —
   * `'pipeline'` is intentionally excluded because the Gitless page does
   * not surface a Pipeline option in the dropdown (canonical-source is a
   * git concept). A compile-time error here keeps a future contributor
   * from forwarding a cross-page-contaminating value. The target member's
   * identity is intentionally not sent.
   */
  public gitlessFilterByMember(filterType: 'member' | 'no-data' | 'unknown') {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.GITLESS_FILTER_BY_MEMBER,
      data: { ...this.defaultData, filterType }
    });
  }

  /**
   * User clicked a session/scan row to drill into issues.
   *
   * @param hasPreviousSession Whether the clicked session has a previous
   *   session to compare against. Distinguishes "investigates a
   *   regression" (true) from "reviews a first/orphan run" (false).
   *   Derived at the call site from `session.session_id_new !==
   *   session.session_id_old`; no member identity or PII.
   */
  public sessionSelect(hasPreviousSession: boolean) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.SESSION_SELECT,
      data: { ...this.defaultData, hasPreviousSession }
    });
  }

  /**
   * Emits a Projects-list row event. The falsy-`projectId` guard lives here,
   * at the class boundary, so no event fires for a row without a real project
   * regardless of caller.
   */
  private sendProjectListRowEvent(
    event: AmplitudeEvents,
    row: ProjectListRowAnalytics
  ) {
    if (!row.projectId) {
      return;
    }
    return this.sendAmplitudeEvent({
      event,
      data: { ...this.defaultData, ...row }
    });
  }

  /**
   * User opened a project from the Projects list — via the row's project-name
   * link or its "Open project" link.
   */
  public projectOpenFromList(row: ProjectListRowAnalytics) {
    return this.sendProjectListRowEvent(
      AmplitudeEvents.PROJECT_OPEN_FROM_LIST,
      row
    );
  }

  /**
   * User opened a project's Configure page from the Projects list row menu.
   */
  public projectConfigFromList(row: ProjectListRowAnalytics) {
    return this.sendProjectListRowEvent(
      AmplitudeEvents.PROJECT_CONFIG_FROM_LIST,
      row
    );
  }

  /**
   * User opened a project's Members page from the Projects list row menu.
   */
  public projectMembersFromList(row: ProjectListRowAnalytics) {
    return this.sendProjectListRowEvent(
      AmplitudeEvents.PROJECT_MEMBERS_FROM_LIST,
      row
    );
  }

  /**
   * @param sessionId The MobileAnalyzer session's id.
   * @param scanCount Number of scans in the session.
   * @param totalIssues Total issues found across the session.
   * @param sessionName Optional human-given session name; omitted from the
   *   payload when not provided.
   */
  public mobileAnalyzerValidSession(
    sessionId: string,
    scanCount: number,
    totalIssues: number,
    sessionName?: string
  ) {
    const data: Record<string, unknown> = {
      ...this.defaultData,
      sessionId,
      scanCount,
      totalIssues
    };

    if (sessionName) {
      data.sessionName = sessionName;
    }

    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.MOBILE_ANALYZER_VALID_SESSION,
      data
    });
  }

  /**
   * The Issue groups tab was selected. `issueGroupsFormed` is carried alongside
   * `issueGroupCount` rather than derived from it in a dashboard, so a scan that
   * legitimately forms no groups is distinguishable at a glance.
   */
  public issueGroupsViewed(
    projectType: string,
    issueGroupCount: number,
    issueGroupsFormed: boolean
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_GROUPS_VIEW,
      data: {
        ...this.defaultData,
        projectType,
        issueGroupCount,
        issueGroupsFormed
      }
    });
  }

  /**
   * A group's own page was opened. The distinct URL and rule counts say
   * whether the groups people drill into span pages, rules, or both.
   */
  public issueGroupOpened(
    issueCount: number,
    urlCount: number,
    ruleCount: number
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_GROUPS_OPEN,
      data: { ...this.defaultData, issueCount, urlCount, ruleCount }
    });
  }

  /** Grouped or Ungrouped was selected inside the Issue groups tab. */
  public issueGroupsSubViewSelected(
    subView: 'grouped' | 'ungrouped',
    issueGroupCount: number,
    issueCount: number
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_GROUPS_SUB_VIEW_SELECT,
      data: { ...this.defaultData, subView, issueGroupCount, issueCount }
    });
  }

  public issuesFilterApplied(
    filterType: IssuesFilterType,
    filterCount: number,
    details?: {
      action: IssuesFilterAction;
      source: IssuesFilterChangeSource;
      view: IssuesAnalyticsView;
    }
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUES_FILTER_APPLIED,
      data: { ...this.defaultData, filterType, filterCount, ...details }
    });
  }

  public issuesViewByTabChange(tab: ViewByTab, previousTab: ViewByTab) {
    if (tab === previousTab) {
      return;
    }
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUES_VIEW_BY_TAB_CHANGE,
      data: { ...this.defaultData, tab, previousTab }
    });
  }

  /** @param filterCount Active filter categories when the drawer opened. */
  public issuesAllFiltersOpen(view: IssuesAnalyticsView, filterCount: number) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUES_ALL_FILTERS_OPEN,
      data: { ...this.defaultData, view, filterCount }
    });
  }

  /** @param direction The direction after the change. */
  public issuesSortChange(
    property: PropertyConfiguration['type'],
    direction: Exclude<SortDirection, 'none'>,
    view: IssuesAnalyticsView
  ) {
    const column = SORT_COLUMNS.get(property);
    if (!column) {
      return;
    }
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUES_SORT_CHANGED,
      data: {
        ...this.defaultData,
        column,
        direction: direction === SortDirections.Ascending ? 'asc' : 'desc',
        view
      }
    });
  }

  public ruleViewIssues(issueCount: number, filterApplied: boolean) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.RULE_VIEW_ISSUES,
      data: { ...this.defaultData, source: 'rule', issueCount, filterApplied }
    });
  }

  public issueOpen(view: IssuesAnalyticsView, trigger: IssueOpenTrigger) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_OPEN,
      data: { ...this.defaultData, view, trigger }
    });
  }

  public issueFlyoutClose(
    view: IssuesAnalyticsView,
    method: IssueFlyoutCloseMethod
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_FLYOUT_CLOSE,
      data: { ...this.defaultData, view, method }
    });
  }

  public issueOpenInNewTab(view: IssuesAnalyticsView) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUE_OPEN_IN_NEW_TAB,
      data: { ...this.defaultData, view }
    });
  }

  public issuesCopyLink(view: IssuesAnalyticsView, filterCount: number) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUES_COPY_LINK,
      data: {
        ...this.defaultData,
        view,
        filterApplied: filterCount > 0,
        filterCount
      }
    });
  }

  public issuesExportModalOpen(viewLevel: IssuesExportViewLevel) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUES_EXPORT_MODAL_OPEN,
      data: { ...this.defaultData, viewLevel }
    });
  }

  public issuesExportFail(
    format: IssuesExportFormat,
    viewLevel: IssuesExportViewLevel,
    reason: IssuesExportFailureReason,
    durationMs: number
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.ISSUES_EXPORT_FAIL,
      data: { ...this.defaultData, format, viewLevel, reason, durationMs }
    });
  }

  /**
   * An empty / zero-row state was shown to the user. Call sites fire this once
   * per mount of the empty-state UI (debounce across re-renders) — see
   * `EmptyStateTracker`. The meaning of "empty" is page-specific; see the
   * per-context notes on `AmplitudeEvents.EMPTY_STATE_SHOWN`.
   *
   * @param context Which page's empty state was shown.
   */
  public emptyStateShown(context: EmptyStateContext) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.EMPTY_STATE_SHOWN,
      data: { ...this.defaultData, context }
    });
  }

  public mobileConfigMismatchBannerShown(
    reason: 'config_changed' | 'legacy_sdk',
    isCanonicalSource: boolean | null
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.MOBILE_CONFIG_MISMATCH_BANNER_SHOWN,
      data: {
        ...this.defaultData,
        reason,
        pipelineState: pipelineState(isCanonicalSource)
      }
    });
  }

  /**
   * User clicked "Add new project" in the Projects list header. An
   * entry-point event with no project context (no project is selected yet).
   */
  public projectAddNewFromList() {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_ADD_NEW_FROM_LIST,
      data: { ...this.defaultData }
    });
  }

  /**
   * User changed the Projects list sort (column header or sort menu).
   *
   * @param sortBy Column key sorted by (e.g. `'name'`, `'last_run'`, `'type'`).
   * @param sortDirection Resulting direction after the change.
   */
  public projectsSortChanged(sortBy: string, sortDirection: 'asc' | 'desc') {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECTS_SORT_CHANGED,
      data: { ...this.defaultData, sortBy, sortDirection }
    });
  }

  /**
   * User applied a search on the Projects list (fires once per settled,
   * non-empty query — never on clear). The raw query is never sent.
   *
   * @param resultCount Number of projects matching after the filter applied.
   */
  public projectsSearchApplied(resultCount: number) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECTS_SEARCH_APPLIED,
      data: { ...this.defaultData, hasActiveSearch: true, resultCount }
    });
  }

  /**
   * User navigated to a different page of the Projects list.
   *
   * @param direction `'next'` / `'previous'` for the step controls,
   *   `'jump'` for the first/last-page controls.
   * @param pageNumber Destination page, 1-indexed.
   */
  public projectsPaginate(
    direction: 'next' | 'previous' | 'jump',
    pageNumber: number
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECTS_PAGINATE,
      data: { ...this.defaultData, direction, pageNumber }
    });
  }

  /**
   * Fired when the user starts project creation from the Projects page.
   *
   * @param source Which entry point launched the wizard: `'projects-page'`
   *   (the header "Add new project" button) or `'empty-state'` (the
   *   first-project CTA shown when the user has no projects yet). Lets us
   *   compare conversion from each entry point into the wizard funnel.
   */
  public projectCreateStart(source: 'projects-page' | 'empty-state') {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_CREATE_START,
      data: { ...this.defaultData, source }
    });
  }

  /**
   * Fired when the user picks a card on the wizard's "Choose your integration
   * method" step.
   *
   * @param projectType The selected `ProjectType` slug — the same value
   *   the wizard writes to its `source=` URL param (e.g.
   *   `axe-devtools-watcher`). The ticket originally named the property's
   *   vocabulary `github`/`gitlab`/`cli`/`sdk`, but no such provider/CLI-vs-SDK
   *   choice exists in this wizard; the integration cards select a project
   *   type, so we ship that slug. Re-fires on every card click, including a
   *   change of selection after going back. Standardized property name across
   *   the wizard funnel (issue #15162); previously emitted as `integrationType`.
   */
  public wizardIntegrationSelected(projectType: ProjectType) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.WIZARD_INTEGRATION_SELECTED,
      data: { ...this.defaultData, projectType }
    });
  }

  /**
   * Fired when the user actively picks a language radio on the wizard's
   * "Language" step. Only the Watcher flow has a language step today, so this
   * currently fires for Watcher projects only. Re-fires on every pick,
   * including changing the selection after going back.
   *
   * Fires on explicit selection only — accepting the prefilled default
   * language without touching the radios does NOT fire this event (the
   * effective language is still captured by `createdProject` at submit time).
   * The funnel is therefore read as "users who actively chose a language", not
   * total coverage.
   *
   * @param language The selected automation language.
   * @param projectType The `ProjectType` whose wizard the selection happened
   *   in. Kept on the payload so the wizard funnel is sliceable by integration
   *   without a join (the wizard is not project-scoped, so `defaultData` does
   *   not carry it here). Standardized property name across the wizard funnel
   *   (issue #15162); previously emitted as `sourceApp`.
   */
  public projectLanguageSelected(
    language: AutomationSupportedLanguages,
    projectType: ProjectType
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_LANGUAGE_SELECTED,
      data: { ...this.defaultData, language, projectType }
    });
  }

  /**
   * Fired when the user actively picks a testing platform on the wizard's
   * "Platform" (Watcher) or "Framework" (Mobile) step — both feed the same
   * selected platform value. Re-fires on every pick.
   *
   * Fires on explicit selection only — accepting the prefilled default
   * platform without touching the radios does NOT fire this event (the
   * effective platform is still captured by `createdProject` at submit time).
   *
   * @param platform The selected platform/framework label (e.g. `Cypress`).
   * @param projectType The `ProjectType` whose wizard the selection happened
   *   in. Kept on the payload so the wizard funnel is sliceable by integration
   *   without a join (the wizard is not project-scoped, so `defaultData` does
   *   not carry it here). Standardized property name across the wizard funnel
   *   (issue #15162); previously emitted as `sourceApp`.
   * @param language The language chosen earlier in the flow, when present
   *   (Watcher only). Omitted for flows with no language step (Mobile) so the
   *   property is absent rather than a literal `undefined`. The value
   *   originates from a URL query param (`getStartupParams` casts it without
   *   validation), so it is checked against the `AutomationSupportedLanguages`
   *   closed set here — an unrecognized value is dropped rather than emitted to
   *   Amplitude, keeping the property aligned with its enum regardless of the
   *   caller.
   */
  public projectPlatformSelected(
    platform: string,
    projectType: ProjectType,
    language?: AutomationSupportedLanguages
  ) {
    const hasValidLanguage =
      language !== undefined &&
      (Object.values(AutomationSupportedLanguages) as string[]).includes(
        language
      );
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_PLATFORM_SELECTED,
      data: {
        ...this.defaultData,
        platform,
        projectType,
        ...(hasValidLanguage ? { language } : {})
      }
    });
  }

  /**
   * Create and edit share the step, so `context` is the only way to tell them
   * apart. `projectType` is passed because `defaultData` is not project-scoped.
   * `previousMode` is the stored value an edit replaces; `null` on create.
   */
  public projectUniqueUrlsSelected(
    mode: UrlMode,
    context: 'create' | 'edit',
    projectType: ProjectType,
    previousMode: UrlMode | null
  ) {
    return this.sendAmplitudeEvent({
      event: AmplitudeEvents.PROJECT_UNIQUE_URLS_SELECT,
      data: { ...this.defaultData, mode, context, projectType, previousMode }
    });
  }

  // Whether the currently-scoped project is a mobile project type. Single
  // source of truth shared by `defaultData` (which ships it as
  // `isMobileProject`) and `viewedIssuesPage` (which uses it to gate the
  // mobile-only accessibility-score enrichment).
  private get isMobileProject(): boolean {
    const projectType = this.project?.selected_project_type;
    return (
      typeof projectType === 'string' &&
      (MOBILE_PROJECT_TYPES as readonly string[]).includes(projectType)
    );
  }

  // Maps the tracked project's `ProjectType` to the analytics-plan
  // `integrationMethod` value. `undefined` when no project is scoped or the
  // type isn't one of the known integration methods, so callers omit the
  // property rather than ship a literal `undefined`.
  private get integrationMethod(): IntegrationMethod | undefined {
    switch (this.project?.selected_project_type) {
      case ProjectType.MobileAnalyzer:
        return IntegrationMethod.MobileAnalyzer;
      case ProjectType.Mobile:
        return IntegrationMethod.MobileSdks;
      case ProjectType.Watcher:
        return IntegrationMethod.Watcher;
      case ProjectType.Html:
        return IntegrationMethod.WebApis;
      default:
        return undefined;
    }
  }

  private get defaultData() {
    const data: Record<string, unknown> = {};

    if (this.enterprise) {
      // We want `organization` set, but that's handled by the base class (in identify).
      data.rootGroup = this.enterprise.root_group;
      data.enterpriseId = this.enterprise.id;
    }

    if (this.project) {
      data.projectId = this.project.project_id;

      data.projectType = this.project.selected_project_type;
      data.isMobileProject = this.isMobileProject;

      // Optional fields are merged only when the source key is supplied so
      // absent fields are omitted from the payload rather than sent as
      // `undefined` (single-project APIs strip some of these).
      for (const [src, dest] of OPTIONAL_PROJECT_FIELD_MAP) {
        const value = this.project[src];
        if (value !== undefined) {
          data[dest] = value;
        }
      }

      // `membership_role` is `role | null`; treat `null` like absent so we
      // don't ship a literal null to Amplitude.
      if (this.project.membership_role) {
        data.userProjectRole = this.project.membership_role;
      }
    }

    return data;
  }
}

export default AxeDevtoolsWatcherAnalytics;
